import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { CheckCircle2, Circle, ChevronLeft, ChevronRight, Clock, FileText, Image as ImageIcon, Video, Link as LinkIcon, List, PenTool, File, Table as TableIcon, Loader2, Check, X, Presentation, Workflow, Images, Sparkles } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCurrentUserId } from "@/hooks/use-current-user";
import { EnhancedVideoPlayer } from "./EnhancedVideoPlayer";
import { SlidePresentation } from "./SlidePresentation";
import { PDFViewer } from "./PDFViewer";
import { FlowchartViewer } from "./FlowchartViewer";
import { GalleryCarousel } from "./GalleryCarousel";
import { WhiteboardActivity } from "./WhiteboardActivity";
import { createSafeHtml } from "@/lib/sanitize";
interface ActivityModalProps {
  isOpen: boolean;
  onClose: () => void;
  activity: any;
  courseId: string;
  allActivities: any[];
  onNavigate: (activityId: string) => void;
  onQuizComplete?: (activityId: string) => void;
  onVideoComplete?: (activityId: string) => void;
  onActivityComplete?: (activityId: string) => void;
}
export function ActivityModal({
  isOpen,
  onClose,
  activity,
  courseId,
  allActivities,
  onNavigate,
  onQuizComplete,
  onVideoComplete,
  onActivityComplete
}: ActivityModalProps) {
  const {
    toast
  } = useToast();
  const queryClient = useQueryClient();
  const [notes, setNotes] = useState("");
  const [activeTab, setActiveTab] = useState("content");
  const [quizAnswers, setQuizAnswers] = useState<Record<number, string | number[]>>({});
  const [hasAutoCompleted, setHasAutoCompleted] = useState(false);
  const [shuffledOptions, setShuffledOptions] = useState<Record<number, number[]>>({});
  const [isAcknowledged, setIsAcknowledged] = useState(false);
  const { data: currentUserId } = useCurrentUserId();

  // Get current activity index
  const currentIndex = allActivities.findIndex(a => a.id === activity?.id);
  const hasNext = currentIndex < allActivities.length - 1;
  const hasPrevious = currentIndex > 0;

  // Fetch completion status
  const {
    data: completion,
    isLoading: loadingCompletion
  } = useQuery({
    queryKey: ['activity-completion', activity?.id],
    queryFn: async () => {
      if (!activity?.id || !currentUserId) return null;
      const {
        data,
        error
      } = await (supabase.from('activity_completions') as any).select('id, user_id, activity_id, course_id, completed_at, notes').eq('user_id', currentUserId).eq('activity_id', activity.id).maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: isOpen && !!activity?.id && !!currentUserId
  });

  // Load notes when completion changes
  useEffect(() => {
    if (completion?.notes) {
      setNotes(completion.notes);
    } else {
      setNotes("");
    }
  }, [completion]);

  // Reset quiz state when modal closes or activity changes
  useEffect(() => {
    if (!isOpen) {
      setQuizAnswers({});
      setActiveTab("content");
      setHasAutoCompleted(false);
      setIsAcknowledged(false);
    }
  }, [isOpen]);

  // Reset quiz state when activity changes
  useEffect(() => {
    setQuizAnswers({});
    setHasAutoCompleted(false);
    setIsAcknowledged(false);
  }, [activity?.id]);

  // Check if acknowledgement already completed
  useEffect(() => {
    if (activity?.activity_type === 'acknowledgement' && completion) {
      setIsAcknowledged(true);
    }
  }, [completion, activity?.activity_type]);

  // Toggle completion mutation
  const toggleCompletionMutation = useMutation({
    mutationFn: async () => {
      if (!currentUserId) throw new Error("Not authenticated");
      if (completion) {
        // Remove completion
        const {
          error
        } = await supabase.from('activity_completions').delete().eq('id', completion.id);
        if (error) throw error;
      } else {
        // Add completion
        const {
          error
        } = await (supabase.from('activity_completions') as any).insert({
          user_id: currentUserId,
          activity_id: activity.id,
          course_id: courseId,
          notes: notes || null
        });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['activity-completions']
      });
      queryClient.invalidateQueries({
        queryKey: ['course-progress']
      });
      queryClient.invalidateQueries({
        queryKey: ['course-view']
      });
      toast({
        title: completion ? "Marked as incomplete" : "Marked as complete",
        description: completion ? "Activity unmarked" : "Great job! Keep going!"
      });
      // Notify parent of completion for local state update
      if (!completion && activity?.id) {
        onActivityComplete?.(activity.id);
      }
      // Close dialog when marking as complete (but not for quizzes - user closes manually)
      if (!completion && activity?.activity_type !== 'quiz') {
        onClose();
      }
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    }
  });

  // Save notes mutation
  const saveNotesMutation = useMutation({
    mutationFn: async () => {
      if (!currentUserId) throw new Error("Not authenticated");
      if (completion) {
        const {
          error
        } = await supabase.from('activity_completions').update({
          notes
        }).eq('id', completion.id);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['activity-completion']
      });
      toast({
        title: "Notes saved",
        description: "Your notes have been saved successfully"
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error saving notes",
        description: error.message,
        variant: "destructive"
      });
    }
  });

  // Check if quiz answers are all correct (pure function, no side effects)
  const isQuizComplete = () => {
    if (!activity || activity.activity_type !== 'quiz') return true;
    
    // Support both single question and multiple questions format
    let questions = [];
    const content = activity.content || {};
    if (content.questions && Array.isArray(content.questions)) {
      questions = content.questions;
    } else if (content.question) {
      questions = [{
        question: content.question,
        options: content.options || [],
        correctAnswer: content.correctAnswer,
        correctAnswers: content.correctAnswers,
        explanation: content.explanation
      }];
    }
    
    if (questions.length === 0) return true;
    if (Object.keys(quizAnswers).length !== questions.length) return false;
    
    return questions.every((q: any, index: number) => {
      const userAnswer = quizAnswers[index];
      const correctAnswers = q.correctAnswers || (q.correctAnswer !== undefined ? [q.correctAnswer] : [0]);
      
      // For multiple correct answers
      if (Array.isArray(userAnswer)) {
        if (userAnswer.length !== correctAnswers.length) return false;
        const sortedUser = [...userAnswer].sort();
        const sortedCorrect = [...correctAnswers].sort();
        return sortedUser.every((ans, i) => ans === sortedCorrect[i]);
      }
      
      // For single answer (backward compatibility)
      return correctAnswers.includes(Number(userAnswer));
    });
  };

  // Mark quiz complete when all questions are answered (no auto-close)
  useEffect(() => {
    if (!activity || activity.activity_type !== 'quiz') return;
    if (hasAutoCompleted || completion) return;
    
    const content = activity.content || {};
    let questions: any[] = [];
    if (content.questions && Array.isArray(content.questions)) {
      questions = content.questions;
    } else if (content.question) {
      questions = [content];
    }
    
    if (questions.length === 0) return;
    if (Object.keys(quizAnswers).length < questions.length) return;
    
    setHasAutoCompleted(true);
    toggleCompletionMutation.mutate();
    
    if (onQuizComplete) {
      onQuizComplete(activity.id);
    }
  }, [quizAnswers, activity, completion, hasAutoCompleted, onQuizComplete]);
  const handleNavigate = (direction: 'next' | 'previous') => {
    const newIndex = direction === 'next' ? currentIndex + 1 : currentIndex - 1;
    if (newIndex >= 0 && newIndex < allActivities.length) {
      onNavigate(allActivities[newIndex].id);
      setActiveTab("content"); // Reset to content tab
    }
  };
  const getActivityIcon = (type: string) => {
    const icons: Record<string, any> = {
      text: FileText,
      video: Video,
      image: ImageIcon,
      gallery: Images,
      link: LinkIcon,
      document: File,
      table: TableIcon,
      quiz: List,
      assignment: PenTool,
      slides: Presentation,
      flowchart: Workflow,
      acknowledgement: CheckCircle2,
      whiteboard: Sparkles
    };
    return icons[type] || FileText;
  };
  const renderContent = () => {
    if (!activity) return null;
    const type = activity.activity_type || activity.activityType;
    const content = activity.content || {};
    switch (type) {
      case 'text':
        return <div className="prose prose-sm max-w-none dark:prose-invert ql-editor">
            {content.text ? <div dangerouslySetInnerHTML={createSafeHtml(content.text)} /> : <p className="text-muted-foreground">No content available</p>}
          </div>;
      case 'video':
        // Extract YouTube video ID from various URL formats
        const getYouTubeVideoId = (url: string) => {
          if (!url) return null;
          const patterns = [
            /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
            /youtube\.com\/watch\?.*v=([^&\n?#]+)/
          ];
          for (const pattern of patterns) {
            const match = url.match(pattern);
            if (match) return match[1];
          }
          return null;
        };
        
        const youtubeId = getYouTubeVideoId(content.youtubeUrl);
        
        return <div className="space-y-4">
            {youtubeId ? (
              <div className="aspect-video w-full">
                <iframe
                  className="w-full h-full rounded-lg"
                  src={`https://www.youtube.com/embed/${youtubeId}`}
                  title={activity.title}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                />
              </div>
            ) : content.url ? (
              <EnhancedVideoPlayer 
                url={content.url} 
                onEnded={() => {
                  if (!completion) {
                    toggleCompletionMutation.mutate();
                  }
                  // Notify parent that video is completed
                  if (onVideoComplete) {
                    onVideoComplete(activity.id);
                  }
                }}
                savedBookmarks={content.bookmarks || []}
                onBookmarksChange={(bookmarks) => {
                  // Save bookmarks to activity content
                  // This could be extended to save to database
                }}
              />
            ) : (
              <p className="text-muted-foreground">No video URL provided</p>
            )}
          </div>;
      case 'image':
        return <div className="space-y-4">
            {content.url ? <img loading="lazy" src={content.url} alt={activity.title} className="w-full rounded-lg border" /> : <p className="text-muted-foreground">No image URL provided</p>}
          </div>;
      case 'gallery':
        return <div className="space-y-4">
            {content.images && Array.isArray(content.images) && content.images.length > 0 ? (
              <GalleryCarousel images={content.images} />
            ) : (
              <p className="text-muted-foreground">No images in this gallery</p>
            )}
          </div>;
      case 'document':
        const getFileName = (url: string) => {
          try {
            const urlObj = new URL(url);
            const pathParts = urlObj.pathname.split('/');
            return pathParts[pathParts.length - 1] || 'document';
          } catch {
            return 'document';
          }
        };
        
        return <div className="space-y-4">
            {content.url ? (
              content.url.toLowerCase().endsWith('.pdf') ? (
                <PDFViewer 
                  url={content.url}
                  onComplete={() => {
                    if (!completion) {
                      toggleCompletionMutation.mutate();
                    }
                  }}
                />
              ) : (
                <div className="border rounded-lg p-6 text-center space-y-4">
                  <File className="h-12 w-12 mx-auto text-primary" />
                  <div className="space-y-2">
                    <p className="font-medium">{activity.title}</p>
                    <p className="text-sm text-muted-foreground">Click below to download this document</p>
                  </div>
                  <Button 
                    onClick={async () => {
                      try {
                        const response = await fetch(content.url);
                        const blob = await response.blob();
                        const downloadUrl = window.URL.createObjectURL(blob);
                        const link = document.createElement('a');
                        link.href = downloadUrl;
                        link.download = getFileName(content.url);
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                        window.URL.revokeObjectURL(downloadUrl);
                        
                        toast({
                          title: "Download started",
                          description: "Your document is downloading"
                        });
                      } catch (error) {
                        toast({
                          title: "Download failed",
                          description: "Please try again or contact support",
                          variant: "destructive"
                        });
                      }
                    }}
                  >
                    <File className="h-4 w-4 mr-2" />
                    Download Document
                  </Button>
                </div>
              )
            ) : <p className="text-muted-foreground">No document URL provided</p>}
          </div>;
      case 'link':
        return <div className="space-y-4">
            {content.url ? <div className="border rounded-lg p-6 space-y-4">
                <LinkIcon className="h-8 w-8 text-primary" />
                <div>
                  <p className="font-medium">External Resource</p>
                  <p className="text-sm text-muted-foreground break-all">{content.url}</p>
                </div>
                <Button asChild>
                  <a href={content.url} target="_blank" rel="noopener noreferrer">
                    Open Link
                  </a>
                </Button>
              </div> : <p className="text-muted-foreground">No link URL provided</p>}
          </div>;
      case 'table':
        return <div className="space-y-4">
            {content.columns && content.rows ? <div className="overflow-x-auto border rounded-lg">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-muted/50">
                      {content.columns.map((col: string, colIndex: number) => <th key={colIndex} className="border-r border-b border-border p-3 text-left text-sm font-semibold">
                          {col}
                        </th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {content.rows.map((row: string[], rowIndex: number) => <tr key={rowIndex} className="hover:bg-accent/10">
                        {row.map((cell: string, cellIndex: number) => <td key={cellIndex} className="border-r border-b border-border p-3 text-sm">
                            {cell}
                          </td>)}
                      </tr>)}
                  </tbody>
                </table>
              </div> : <p className="text-muted-foreground">No table data available</p>}
          </div>;
      case 'quiz':
        // Support both single question and multiple questions format
        let questions = [];
        if (content.questions && Array.isArray(content.questions)) {
          // Multi-question format — normalize true/false questions to have options
          questions = content.questions.map((q: any) => {
            if (q.type === 'true_false' && !q.options) {
              return {
                ...q,
                options: ['True', 'False'],
                correctAnswer: q.correctAnswer === true ? 0 : 1,
              };
            }
            return { ...q, options: q.options || [] };
          });
        } else if (content.question) {
          // Single question format - convert to array
          questions = [{
            question: content.question,
            options: content.options || [],
            correctAnswer: content.correctAnswer,
            correctAnswers: content.correctAnswers,
            explanation: content.explanation,
            shuffleOptions: content.shuffleOptions
          }];
        }
        
        // Shuffle options if needed
        useEffect(() => {
          const newShuffled: Record<number, number[]> = {};
          questions.forEach((q: any, qIndex: number) => {
            if (q.shuffleOptions && q.options && !shuffledOptions[qIndex]) {
              const indices = Array.from({ length: q.options.length }, (_, i) => i);
              // Fisher-Yates shuffle
              for (let i = indices.length - 1; i > 0; i--) {
                const j = Math.floor(Math.random() * (i + 1));
                [indices[i], indices[j]] = [indices[j], indices[i]];
              }
              newShuffled[qIndex] = indices;
            }
          });
          if (Object.keys(newShuffled).length > 0) {
            setShuffledOptions(prev => ({ ...prev, ...newShuffled }));
          }
        }, [questions.length]);
        
        const getFontClass = (text: string) => {
          const upperText = text.toUpperCase();
          if (upperText.includes('LATO')) return 'font-lato';
          if (upperText.includes('FREDOKA')) return 'font-fredoka';
          if (upperText.includes('GLACIAL')) return 'font-glacial';
          return '';
        };
        const allAnswered = Object.keys(quizAnswers).length === questions.length;
        return <div className="space-y-6">

            {questions.map((question: any, qIndex: number) => {
            const correctAnswers = question.correctAnswers || (question.correctAnswer !== undefined ? [question.correctAnswer] : [0]);
            const isMultipleChoice = correctAnswers.length > 1;
            const userAnswer = quizAnswers[qIndex];
            
            const options = question.options || [];
            // Get display order (shuffled or original)
            const displayOrder = question.shuffleOptions && shuffledOptions[qIndex] 
              ? shuffledOptions[qIndex] 
              : Array.from({ length: options.length }, (_, i) => i);
            
            // Check if answer is correct
            const isCorrect = (() => {
              if (Array.isArray(userAnswer)) {
                if (userAnswer.length !== correctAnswers.length) return false;
                const sortedUser = [...userAnswer].sort();
                const sortedCorrect = [...correctAnswers].sort();
                return sortedUser.every((ans, i) => ans === sortedCorrect[i]);
              }
              return correctAnswers.includes(Number(userAnswer));
            })();
            
            const showFeedback = Array.isArray(userAnswer) ? userAnswer.length > 0 : !!userAnswer;
            
            return <div key={qIndex} className={`border rounded-lg p-6 space-y-4 ${showFeedback ? isCorrect ? 'bg-success/5 border-success' : 'bg-destructive/5 border-destructive' : ''}`}>
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-semibold">
                      {qIndex + 1}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-base mb-4">{question.question}</p>
                      
                      {isMultipleChoice ? (
                        <div className="space-y-3">
                          <p className="text-xs text-muted-foreground mb-2">Select all correct answers</p>
                          {displayOrder.map((originalIndex: number) => {
                            const option = options[originalIndex];
                            const isThisCorrect = correctAnswers.includes(originalIndex);
                            const isSelected = Array.isArray(userAnswer) && userAnswer.includes(originalIndex);
                            return <div key={originalIndex} className={`flex items-center space-x-3 p-3 rounded-lg border transition-colors ${showFeedback && isThisCorrect ? 'bg-success/20 border-success' : showFeedback && isSelected && !isCorrect ? 'bg-destructive/20 border-destructive' : 'hover:bg-accent/50'}`}>
                                <Checkbox 
                                  checked={isSelected}
                                  onCheckedChange={(checked) => {
                                    setQuizAnswers(prev => {
                                      const current = Array.isArray(prev[qIndex]) ? prev[qIndex] as number[] : [];
                                      const newAnswer = checked 
                                        ? [...current, originalIndex]
                                        : current.filter((idx: number) => idx !== originalIndex);
                                      return { ...prev, [qIndex]: newAnswer };
                                    });
                                  }}
                                  id={`q${qIndex}-o${originalIndex}`}
                                />
                                <label htmlFor={`q${qIndex}-o${originalIndex}`} className={`flex-1 cursor-pointer text-sm ${getFontClass(option)}`}>
                                  <span className="font-semibold mr-2 mx-[3px] px-[3px]">{String.fromCharCode(65 + displayOrder.indexOf(originalIndex))}.</span>
                                  {option}
                                </label>
                                {showFeedback && isThisCorrect && <Check className="h-5 w-5 text-success" />}
                                {showFeedback && isSelected && !isThisCorrect && <X className="h-5 w-5 text-destructive" />}
                              </div>;
                          })}
                        </div>
                      ) : (
                        <RadioGroup value={typeof userAnswer === 'string' ? userAnswer : String(userAnswer || "")} onValueChange={value => {
                          setQuizAnswers(prev => ({
                            ...prev,
                            [qIndex]: value
                          }));
                        }} className="space-y-3">
                          {displayOrder.map((originalIndex: number) => {
                            const option = options[originalIndex];
                            const optionValue = String(originalIndex);
                            const isThisCorrect = correctAnswers.includes(originalIndex);
                            const isSelected = String(userAnswer) === optionValue;
                            return <div key={originalIndex} className={`flex items-center space-x-3 p-3 rounded-lg border transition-colors ${showFeedback && isThisCorrect ? 'bg-success/20 border-success' : showFeedback && isSelected && !isCorrect ? 'bg-destructive/20 border-destructive' : 'hover:bg-accent/50'}`}>
                                <RadioGroupItem value={optionValue} id={`q${qIndex}-o${originalIndex}`} />
                                <label htmlFor={`q${qIndex}-o${originalIndex}`} className={`flex-1 cursor-pointer text-sm ${getFontClass(option)}`}>
                                  <span className="font-semibold mr-2 mx-[3px] px-[3px]">{String.fromCharCode(65 + displayOrder.indexOf(originalIndex))}.</span>
                                  {option}
                                </label>
                                {showFeedback && isThisCorrect && <Check className="h-5 w-5 text-success" />}
                                {showFeedback && isSelected && !isCorrect && <X className="h-5 w-5 text-destructive" />}
                              </div>;
                          })}
                        </RadioGroup>
                      )}
                    </div>
                  </div>
                  
                  {showFeedback && !isCorrect && (
                    <div className="mt-4 p-3 bg-muted/50 rounded-lg border">
                      <p className="text-xs font-medium mb-1">Explanation:</p>
                      {question.explanation ? (
                        <p className="text-xs text-muted-foreground" dangerouslySetInnerHTML={{ __html: question.explanation }} />
                      ) : (
                        <p className="text-xs text-muted-foreground">
                          The correct answer is <span className="font-semibold">{correctAnswers.map((i: number) => options[i]).join(', ')}</span>.
                          {isMultipleChoice 
                            ? ' Make sure to select all the correct options.'
                            : ` "${options[correctAnswers[0]]}" is the right choice for this question.`}
                        </p>
                      )}
                    </div>
                  )}
                  {showFeedback && isCorrect && question.explanation && (
                    <div className="mt-4 p-3 bg-success/10 rounded-lg border border-success/30">
                      <p className="text-xs font-medium mb-1">Explanation:</p>
                      <p className="text-xs text-muted-foreground" dangerouslySetInnerHTML={{ __html: question.explanation }} />
                    </div>
                  )}
                </div>;
          })}
          </div>;
      case 'slides':
        return <div className="space-y-4">
            {content.pptxUrl ? (
              <div className="border rounded-lg overflow-hidden bg-background">
                <iframe
                  src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(content.pptxUrl as string)}`}
                  className="w-full"
                  style={{ height: 'calc(70vh)' }}
                  frameBorder="0"
                  allowFullScreen
                  title={activity?.title || "Presentation"}
                />
              </div>
            ) : content.slides && Array.isArray(content.slides) && content.slides.length > 0 ? (
              <SlidePresentation 
                slides={content.slides}
                onComplete={() => {
                  if (!completion) {
                    toggleCompletionMutation.mutate();
                  }
                }}
              />
            ) : <p className="text-muted-foreground">No presentation available</p>}
          </div>;
      case 'assignment':
        return <div className="space-y-4">
            <div className="border rounded-lg p-6 space-y-4">
              <PenTool className="h-8 w-8 text-primary" />
              <div>
                <p className="font-medium">Assignment</p>
                {content.text && <div className="prose prose-sm max-w-none dark:prose-invert mt-4">
                    <div dangerouslySetInnerHTML={createSafeHtml(content.text)} />
                  </div>}
              </div>
              <div className="bg-muted/50 rounded-lg p-4">
                <p className="text-sm text-muted-foreground">
                  Assignment submission functionality will be available soon
                </p>
              </div>
            </div>
          </div>;
      case 'flowchart':
        return <div className="space-y-4">
            {content.text ? (
              <FlowchartViewer content={content.text} />
            ) : (
              <p className="text-muted-foreground">No flowchart data available</p>
            )}
          </div>;
      case 'acknowledgement':
        return <div className="space-y-6">
            <div className="border rounded-lg p-6 space-y-6 bg-muted/30">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="h-6 w-6 text-primary mt-1 flex-shrink-0" />
                <div className="space-y-4 flex-1">
                  {content.text && <div className="prose prose-sm max-w-none dark:prose-invert">
                      <div dangerouslySetInnerHTML={createSafeHtml(content.text)} />
                    </div>}
                  
                  <div className="flex items-center space-x-3 pt-4 border-t">
                    <Checkbox 
                      id={`acknowledge-${activity.id}`}
                      checked={isAcknowledged}
                      onCheckedChange={(checked) => {
                        setIsAcknowledged(checked === true);
                        if (checked && !completion) {
                          toggleCompletionMutation.mutate();
                        }
                      }}
                      disabled={completion !== null}
                      className="h-5 w-5"
                    />
                    <label 
                      htmlFor={`acknowledge-${activity.id}`}
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                    >
                      I acknowledge that I have read and understood this information
                    </label>
                  </div>
                  
                  {!isAcknowledged && !completion && (
                    <p className="text-xs text-muted-foreground bg-amber-500/10 text-amber-700 dark:text-amber-400 p-3 rounded-md border border-amber-500/20">
                      ⚠️ You must check the box above to mark this as complete and proceed to the next activity.
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>;
      case 'whiteboard':
        return <div className="space-y-4">
            <WhiteboardActivity 
              welcomeMessage={content.welcomeMessage}
              placeholderText={content.placeholderText}
              examplePrompts={content.examplePrompts}
              onComplete={() => {
                if (!completion) {
                  toggleCompletionMutation.mutate();
                }
              }}
            />
          </div>;
      default:
        return <p className="text-muted-foreground">Unknown activity type</p>;
    }
  };
  if (!activity) return null;
  const Icon = getActivityIcon(activity.activity_type || activity.activityType);
  const isCompleted = !!completion;
  return <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-7xl h-[95vh] max-h-[95vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3 flex-1">
              <div className="p-2 rounded-lg bg-primary/10 mt-1">
                <Icon className="h-5 w-5 text-primary" />
              </div>
              <div className="flex-1 space-y-2">
                <DialogTitle className="text-xl">{activity.title}</DialogTitle>
                {activity.description && <p className="text-sm text-muted-foreground">{activity.description}</p>}
                <div className="flex items-center gap-2 flex-wrap">
                  {activity.duration_minutes && <Badge variant="secondary" className="text-xs">
                      <Clock className="h-3 w-3 mr-1" />
                      {activity.duration_minutes}m
                    </Badge>}
                  {activity.is_required && <Badge variant="outline" className="text-xs">
                      Required
                    </Badge>}
                  {loadingCompletion ? <Badge variant="secondary" className="text-xs">
                      <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                      Loading...
                    </Badge> : isCompleted ? <Badge className="text-xs bg-green-500 hover:bg-green-600">
                      <CheckCircle2 className="h-3 w-3 mr-1" />
                      Completed
                    </Badge> : <Badge variant="outline" className="text-xs">
                      <Circle className="h-3 w-3 mr-1" />
                      Not Started
                    </Badge>}
                </div>
              </div>
            </div>
          </div>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="content">Content</TabsTrigger>
            <TabsTrigger value="notes">My Notes</TabsTrigger>
          </TabsList>

          <TabsContent value="content" className="flex-1 overflow-y-auto mt-4 space-y-4">
            {renderContent()}
          </TabsContent>

          <TabsContent value="notes" className="flex-1 overflow-y-auto mt-4 space-y-4">
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Personal Notes</label>
                <Textarea placeholder="Add your notes about this activity..." value={notes} onChange={e => setNotes(e.target.value)} rows={10} className="resize-none" />
              </div>
              {completion && <Button onClick={() => saveNotesMutation.mutate()} disabled={saveNotesMutation.isPending} className="w-full">
                  {saveNotesMutation.isPending ? <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Saving...
                    </> : "Save Notes"}
                </Button>}
              {!completion && <p className="text-sm text-muted-foreground text-center">
                  Mark this activity as complete to save notes
                </p>}
            </div>
          </TabsContent>
        </Tabs>

        <div className="flex items-center justify-between gap-4 pt-4 border-t">
          {activity?.activity_type === 'quiz' ? (
            <div className="flex-1" />
          ) : (
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => handleNavigate('previous')} disabled={!hasPrevious}>
                <ChevronLeft className="h-4 w-4 mr-1" />
                Previous
              </Button>
              <Button variant="outline" size="sm" onClick={() => handleNavigate('next')} disabled={!hasNext}>
                Next
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          )}

          {activity?.activity_type === 'quiz' ? (
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          ) : (
            <Button onClick={() => toggleCompletionMutation.mutate()} disabled={toggleCompletionMutation.isPending || loadingCompletion} variant={isCompleted ? "outline" : "default"}>
              {toggleCompletionMutation.isPending ? <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Processing...
                </> : isCompleted ? <>
                  <Circle className="h-4 w-4 mr-2" />
                  Mark Incomplete
                </> : <>
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  Mark Complete
                </>}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>;
}